import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { Alert, Button, Card, CardHeader, CardBody, CardFooter, Form, Input, InputGroupText, InputGroup, FormGroup, Label, Row, Col } from 'reactstrap';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';

import { frontend } from '../../../api';
import { validateDate, validateEmailAddress } from '../../../validator';
import { FaMailBulk, FaRegCalendarAlt, FaSpinner, FaUser } from 'react-icons/fa';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';
import { useNavigate } from 'react-router-dom';
import { UnauthorizedException } from 'api/exceptions/UnauthorizedException';
import { InvalidParametersException } from 'api/exceptions/InvalidParametersException';

interface Props {
	userData: {
		emailAddress?: string;
		dob?: string;
		firstName?: string;
		lastName?: string;
		loginToken?: string;
		token?: string;
		uploadedFiles?: { [key: string]: File };
	};
	setUserData: (userData: {
		emailAddress?: string;
		dob?: string;
		firstName?: string;
		lastName?: string;
		loginToken?: string;
		verificationCodeEmail?: string;
		uploadedFiles?: { [key: string]: File };
	}) => void;
}

export const GuestUpload = (props: Props) => {
	const { userData, setUserData } = props;
	const navigate = useNavigate();
	const [checkButtonEnabled, setCheckButtonEnabled] = useState(false);
	const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
	const [clearanceTypes, setClearanceTypes] = useState<Array<{ uid: string; name: string }>>([]);

	useEffect(() => {
		const getContents = async () => {
			try {
				const result = await frontend.getClearanceTypes(userData.token!);

				if (result != null) {
					setClearanceTypes(result);
				}
			} catch (error) {
				if (error instanceof UnauthorizedException) {
					// Redirect to login
					navigate('/app/new-member/login');
				} else if (error instanceof FailedToFetchException) {
					toast.error(error.message);
				} else {
					Sentry.captureException(error);
				}
			}
		};

		getContents();
	}, []);

	useEffect(() => {
		let canSave = true;

		if (userData.firstName == null || userData.firstName === '') {
			canSave = false;
		} else if (userData.lastName == null || userData.lastName === '') {
			canSave = false;
		} else if (userData.dob == null || userData.dob === '') {
			canSave = false;
		} else if (!validateDate(userData.dob!)) {
			canSave = false;
		} else if (userData.emailAddress == null || userData.emailAddress === '') {
			canSave = false;
		} else if (!validateEmailAddress(userData.emailAddress!)) {
			canSave = false;
		} else if (userData.uploadedFiles == null || Object.keys(userData.uploadedFiles).length === 0) {
			canSave = false;
		} else if (canSave) {
			const currentDate = new Date();
			currentDate.setUTCHours(0, 0, 0, 0);
			currentDate.setUTCDate(currentDate.getUTCDate() - 1);

			const oldDate = new Date(1900, 0, 1, 0, 0, 0, 0);

			const dobObj = new Date(userData.dob!);
			if (dobObj.getTime() >= currentDate.getTime() || dobObj.getTime() < oldDate.getTime()) {
				canSave = false;
			}
		}

		setCheckButtonEnabled(canSave);
	}, [userData]);

	const handleFileSelection = (clearanceTypeID: string, acceptedFiles: Array<File>) => {
		const newUserData = { ...userData };

		if (newUserData.uploadedFiles == null) {
			newUserData.uploadedFiles = {};
		}

		newUserData.uploadedFiles[clearanceTypeID] = acceptedFiles[0];

		setUserData(newUserData);
	};

	const handleInputFieldChange = (fieldName: 'firstName' | 'lastName' | 'dob' | 'emailAddress', newValue: any) => {
		const newUserData = { ...userData };
		newUserData[fieldName] = newValue;
		setUserData(newUserData);
	};

	const handleUploadClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setIsSubmitInProgress(true);

		if (userData.uploadedFiles) {
			for (const clearanceUID of Object.keys(userData.uploadedFiles)) {
				const file = userData.uploadedFiles[clearanceUID];

				const formData = new FormData();

				formData.append(
					'file-attributes',
					JSON.stringify({
						type: clearanceUID,
						firstName: userData.firstName,
						lastName: userData.lastName,
						dob: userData.dob,
						emailAddress: userData.emailAddress,
					}),
				);
				formData.append('files', file);

				try {
					const data = await frontend.uploadGuestClearance(userData.token != null ? userData.token : '', formData);
					if (data != null) {
						toast.success(`Successfully uploaded file.`);
					}
				} catch (error: any) {
					if (error instanceof InvalidParametersException) {
						toast.error(error.message);
					} else if (error instanceof FailedToFetchException) {
						toast.error(error.message);
					} else if (error instanceof UnauthorizedException) {
						navigate('/app/new-member/login');
					} else {
						toast.error('Error saving file');
						Sentry.captureException(error);
					}
				}
			}
		}

		setIsSubmitInProgress(false);
	};

	if (userData.token == null) {
		navigate({ pathname: `/app/new-member/login` });
	}

	return (
		<Form action="" className="form" method="" onSubmit={(e) => handleUploadClick(e)}>
			<Card className="card-dashboard">
				<CardHeader>
					<CardHeader>
						<h3 className="header text-center">PA Clearances Portal</h3>
						<h5 className="header text-center">New Member Submission</h5>
					</CardHeader>
				</CardHeader>
				<CardBody>
					<Row>
						<Col sm="6">
							<FormGroup>
								<Label for="bsaId">First Name</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaUser color="#66615b" />
									</InputGroupText>
									<Input
										type="text"
										placeholder="Enter First Name"
										onChange={(e) => handleInputFieldChange('firstName', e.target.value)}
										value={userData.firstName}
									/>
								</InputGroup>
							</FormGroup>
						</Col>
						<Col sm="6">
							<FormGroup>
								<Label for="dob">Last Name</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaRegCalendarAlt color="#66615b" />
									</InputGroupText>
									<Input
										type="text"
										placeholder="Enter Last Name"
										onChange={(e) => handleInputFieldChange('lastName', e.target.value)}
										value={userData.lastName}
									/>
								</InputGroup>
							</FormGroup>
						</Col>
					</Row>
					<Row>
						<Col sm="6">
							<FormGroup>
								<Label for="dob">Date of Birth</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaRegCalendarAlt color="#66615b" />
									</InputGroupText>
									<Input
										type="date"
										placeholder="Enter date of birth"
										onChange={(e) => handleInputFieldChange('dob', e.target.value)}
										value={userData.dob}
									/>
								</InputGroup>
							</FormGroup>
						</Col>

						<Col sm="6">
							<FormGroup>
								<Label for="dob">Email Address</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaMailBulk color="#66615b" />
									</InputGroupText>
									<Input
										type="text"
										placeholder="Enter email address"
										onChange={(e) => handleInputFieldChange('emailAddress', e.target.value)}
										value={userData.emailAddress}
									/>
								</InputGroup>
							</FormGroup>
						</Col>
					</Row>
					<FormGroup>
						<Label for="clearanceUploads">Upload Clearances</Label>
						<Row
							style={{
								alignItems: 'center',
								justifyContent: 'center',
								textAlign: 'center',
							}}
						>
							{clearanceTypes != null && clearanceTypes ? (
								clearanceTypes.map((clearanceType) => {
									return (
										<Dropzone
											key={clearanceType.uid}
											onDrop={(acceptedFiles: Array<File>) => handleFileSelection(clearanceType.uid, acceptedFiles)}
											className="col-sm-2"
											style={{
												minHeight: '180px',
												minWidth: '180px',
												border: '1px solid #ccc',
												margin: '10px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												textAlign: 'center',
											}}
										>
											{clearanceType.name}
										</Dropzone>
									);
								})
							) : (
								<></>
							)}
						</Row>
					</FormGroup>
				</CardBody>
				<CardFooter>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<div style={{ width: '100%' }}>
							<Button
								block
								className="btn-round mb-3"
								color="primary"
								type="button"
								disabled={isSubmitInProgress || (!isSubmitInProgress && !checkButtonEnabled)}
								onClick={(e) => handleUploadClick(e)}
							>
								{isSubmitInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Upload'}
							</Button>
						</div>
					</div>
				</CardFooter>
			</Card>
		</Form>
	);
};

const Dropzone = ({
	onDrop,
	children,
	style,
	className,
}: {
	onDrop: (acceptedFiles: Array<File>) => void;
	children: React.ReactNode;
	style: React.CSSProperties;
	className?: string;
}) => {
	const { getRootProps, getInputProps, acceptedFiles } = useDropzone({ onDrop: onDrop, multiple: true });

	return (
		<div {...getRootProps()} style={{ padding: '30px', outline: 'none', cursor: 'pointer', position: 'relative', ...style }} className={className}>
			<input className="dropzone-input" {...getInputProps()} />
			<div
				className="dropzone-content"
				style={{
					position: 'absolute',
					display: 'flex',
					flexDirection: 'column',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					textAlign: 'center',
				}}
			>
				<i className="fa fa-cloud-upload" style={{ color: '#ccc' }} />
				{children}
				{acceptedFiles != null && acceptedFiles.length > 0 ? <div>{acceptedFiles[0].name}</div> : <></>}
			</div>
		</div>
	);
};
